import { create } from 'zustand';
import type { Point } from '@machina/core';

/**
 * Tool Store
 *
 * Tracks the active map tool and tool-specific state.
 */

export type Tool = 'select' | 'token' | 'measure' | 'fog' | 'draw' | 'dice' | 'ping' | 'spell';

// =============================================================================
// MEASURE TOOL STATE
// =============================================================================

export interface MeasureState {
  isActive: boolean;
  startPoint: Point | null;
  endPoint: Point | null;
  waypoints: Point[]; // For multi-segment paths
  snapToGrid: boolean;
}

// =============================================================================
// MAIN STORE
// =============================================================================

interface ToolState {
  activeTool: Tool;
  setActiveTool: (tool: Tool) => void;

  // Measure tool
  measure: MeasureState;
  startMeasure: (point: Point) => void;
  updateMeasure: (point: Point) => void;
  addWaypoint: (point: Point) => void;
  endMeasure: () => void;
  clearMeasure: () => void;
  toggleMeasureSnap: () => void;
}

export const useToolStore = create<ToolState>((set) => ({
  activeTool: 'select',

  measure: {
    isActive: false,
    startPoint: null,
    endPoint: null,
    waypoints: [],
    snapToGrid: true,
  },

  setActiveTool: (tool) =>
    set((state) => ({
      activeTool: tool,
      // Clear measure when switching tools
      measure: tool !== 'measure' ? { ...state.measure, isActive: false, startPoint: null, endPoint: null, waypoints: [] } : state.measure,
    })),

  startMeasure: (point) =>
    set((state) => ({
      measure: {
        ...state.measure,
        isActive: true,
        startPoint: point,
        endPoint: point,
        waypoints: [],
      },
    })),

  updateMeasure: (point) =>
    set((state) => ({
      measure: {
        ...state.measure,
        endPoint: point,
      },
    })),

  addWaypoint: (point) =>
    set((state) => ({
      measure: {
        ...state.measure,
        waypoints: [...state.measure.waypoints, state.measure.endPoint || point],
        endPoint: point,
      },
    })),

  endMeasure: () =>
    set((state) => ({
      measure: {
        ...state.measure,
        isActive: false,
      },
    })),

  clearMeasure: () =>
    set((state) => ({
      measure: {
        ...state.measure,
        isActive: false,
        startPoint: null,
        endPoint: null,
        waypoints: [],
      },
    })),

  toggleMeasureSnap: () =>
    set((state) => ({
      measure: {
        ...state.measure,
        snapToGrid: !state.measure.snapToGrid,
      },
    })),
}));
